import {
  Box,
  Button,
  Divider,
  Fab,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../../../context/AuthContext";
import { SnackAlertContext } from "../../../../context/SnackAlertContext";
import TopSection from "./TopSection";
import MaterialSection from "./MaterialSection";
import SelectProducts from "./SelectProducts";
import Items from "./Items/Items";
import useVariables from "../../../../hooks/useVariables";
import {
  currencyFormatNoDecimals,
  toFourDecimals,
  toThreeDecimals,
  toTwoDecimals,
} from "../../../../components/NewDetail/calculations/utils";
import { useTheme } from "@emotion/react";
import OrderTitle from "../../../../components/OrderTitle";
import { useNavigate } from "react-router-dom";

const EMPTY_QUOTE = {
  skidCharge: 150,
  isItemizedEstimate: false,
  quoteType: "Confirmed Cut-List",
};

// quoteId: null | number
// onSave: function
const QuoteForm = ({ quoteId, onSave, saveButtonTitle, saving }) => {
  const theme = useTheme();
  const { openSnackMessage } = useContext(SnackAlertContext);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { ezorder, authToken } = useContext(AuthContext);
  const [quote, setQuote] = useState(null);
  const { variables, getDefaultVariables } = useVariables();
  const [loading, setLoading] = useState();
  const navigate = useNavigate();

  // Lag due to whole quote being udpated on every change (all orderitem components recalculated and reloaded)
  // Should add calculation before this and add calculation should be for specific item, not all items
  const onUpdateQuote = (update) => {
    let quoteCopy = { ...quote, ...update };
    let quoteWithNewCalculations = addCalculations(quoteCopy);
    setQuote(quoteWithNewCalculations);
    // setQuote((prev) => ({ ...prev, ...update }));
  };

  const addCalculations = (quoteCopy) => {
    let quoteItems = quoteCopy.items ?? [];
    let itemsCopy = [...quoteItems];
    for (let i = 0; i < itemsCopy.length; i++) {
      let item = itemsCopy[i];
      switch (item.objectType) {
        case "FlatSheet":
          itemsCopy[i] = calculateFlatSheet(quoteCopy, item);
          break;
        case "Coil":
          itemsCopy[i] = calculateCoil(quoteCopy, item);
          break;
        case "Panel":
          itemsCopy[i] = calculatePanel(quoteCopy, item);
          break;
        case "TrimAndFlashing":
          itemsCopy[i] = calculateTrim(quoteCopy, item);
          break;
        case "BenchWork":
          itemsCopy[i] = calculateBenchWork(quoteCopy, item);
          break;
        case "CopingCap":
          itemsCopy[i] = calculateTrim(quoteCopy, item);
          break;
        case "CopingCapCleat":
          itemsCopy[i] = calculateTrim(quoteCopy, item);
          break;
        case "SplicePlate":
          itemsCopy[i] = calculateSplicePlate(quoteCopy, item);
          break;
        case "Accessory":
          // Accessory Calculations Are done inside the Accessory component
          break;
      }
    }

    // Calculate Out the Door Cost
    // - item totalSellPrice + skid charge
    let outTheDoorCost = 0;
    for (let item of itemsCopy) {
      outTheDoorCost += Number(item.totalSellPrice);
    }
    outTheDoorCost += Number(quoteCopy.skidCharge ?? 0);

    return { ...quoteCopy, items: itemsCopy, outTheDoorCost };
  };

  // DONE (Same as in Quote Version 1)
  // The flat sheet item sheet pricing is separate from the order flat sheet pricing
  const calculateFlatSheet = (quoteCopy, flatSheet) => {
    // Flat Sheet Cost For Whole Order
    let { flatSheetCost } = quoteCopy;

    let flatSheetCopy = { ...flatSheet };
    const GROSS_PROFIT_MARGIN = flatSheetCopy.variables.gross_profit_margin;

    // Override Quote FlatSheet Cost with FlathSheetOrderItem flat sheet cost if exists and set as calculated FLAT_SHEET_COST
    let FLAT_SHEET_COST =
      flatSheetCopy.overrides && flatSheetCopy.overrides.cost_per_piece
        ? Number(flatSheetCopy.overrides.cost_per_piece ?? 0)
        : Number(flatSheetCost ?? 0);

    // let FLAT_SHEET_COST = 0;
    // if (
    //   flatSheetCopy.flatSheetCalculations.costPerFlatSheet != undefined &&
    //   flatSheetCopy.flatSheetCalculations.costPerFlatSheet != null &&
    //   !isNaN(flatSheetCopy.flatSheetCalculations.costPerFlatSheet)
    // ) {
    //   FLAT_SHEET_COST = toTwoDecimals(
    //     flatSheetCopy.flatSheetCalculations.costPerFlatSheet
    //   );
    // } else {
    //   FLAT_SHEET_COST = 0;
    // }

    const MATERIAL_COST = toTwoDecimals(
      parseFloat(flatSheetCopy.quantity) * FLAT_SHEET_COST
    );
    const GROSS_PROFIT_DOLLARS =
      MATERIAL_COST / (1 - GROSS_PROFIT_MARGIN) - MATERIAL_COST;
    const TOTAL_SELL_PRICE = MATERIAL_COST + GROSS_PROFIT_DOLLARS;
    const PRICE_PER_FLAT_SHEET = toTwoDecimals(
      TOTAL_SELL_PRICE / flatSheetCopy.quantity
    );

    let flatSheetCalculations = {
      costPerFlatSheet: FLAT_SHEET_COST,
      materialCost: MATERIAL_COST,
      // grossProfitMargin: toFourDecimals(GROSS_PROFIT_MARGIN),
      grossProfitDollars: toTwoDecimals(GROSS_PROFIT_DOLLARS),
      totalSellPrice: toTwoDecimals(TOTAL_SELL_PRICE),
      pricePerFlatSheet: PRICE_PER_FLAT_SHEET,
    };
    return {
      ...flatSheetCopy,
      flatSheetCalculations,
      // TODO: totalCost is deprecated. Migrate to totalSellPrice and pricePerFlatSheet
      totalCost: TOTAL_SELL_PRICE, // TODO: Final Price. May need to migrate this data from calculations for older orders
      totalSellPrice: TOTAL_SELL_PRICE,
      pricePerPiece: PRICE_PER_FLAT_SHEET,
    };
  };

  // DONE (Same as in Quote Version 1)
  const calculateCoil = (quoteCopy, coil) => {
    let coilCopy = { ...coil };
    const GROSS_PROFIT_MARGIN = coilCopy.variables.gross_profit_margin;

    // Coil Cost Based On Color
    let COIL_COST_PER_SQFT = coil.coilCalculations
      ? parseFloat(coil.coilCalculations.costPerSquareFeetOfCoil)
      : 0;
    if (!COIL_COST_PER_SQFT) COIL_COST_PER_SQFT = 0;

    const COST_PER_SQUARE_FEET_OF_COIL = toTwoDecimals(COIL_COST_PER_SQFT); // non-override value

    // (width (in.) / 12) * lineal feet
    const SQUARE_FEET_OF_COIL_NEEDED = toThreeDecimals(
      (parseFloat(coilCopy.width) / 12) * parseFloat(coilCopy.linealFeet)
    );

    // Cost = Square Feet of Coil * Price Per Square Feet of Coil
    const MATERIAL_COST = toTwoDecimals(
      parseFloat(COST_PER_SQUARE_FEET_OF_COIL) *
        parseFloat(SQUARE_FEET_OF_COIL_NEEDED)
    );

    const GROSS_PROFIT_DOLLARS =
      MATERIAL_COST / (1 - GROSS_PROFIT_MARGIN) - MATERIAL_COST;
    const TOTAL_SELL_PRICE = MATERIAL_COST + GROSS_PROFIT_DOLLARS;

    const PRICE_PER_SQUARE_FEET_OF_COIL = toTwoDecimals(
      TOTAL_SELL_PRICE / SQUARE_FEET_OF_COIL_NEEDED
    );

    const PRICE_PER_LINEAL_FOOT_OF_COIL = toTwoDecimals(
      TOTAL_SELL_PRICE / parseFloat(coilCopy.linealFeet)
    );

    let coilCalculations = {
      costPerSquareFeetOfCoil: COST_PER_SQUARE_FEET_OF_COIL,
      squareFeetOfCoil: SQUARE_FEET_OF_COIL_NEEDED,
      materialCost: MATERIAL_COST,
      grossProfitMargin: toFourDecimals(GROSS_PROFIT_MARGIN),
      grossProfitDollars: toTwoDecimals(GROSS_PROFIT_DOLLARS),
      totalSellPrice: toTwoDecimals(TOTAL_SELL_PRICE),
      pricePerSquareFeetOfCoil: PRICE_PER_SQUARE_FEET_OF_COIL,
      pricePerLinealFootOfCoil: PRICE_PER_LINEAL_FOOT_OF_COIL,
    };
    coilCopy.coilCalculations = coilCalculations;
    return {
      ...coilCopy,
      coilCalculations,
      totalCost: TOTAL_SELL_PRICE, // TODO: DEPRECATED, migrate to totalSellPrice
      totalSellPrice: toTwoDecimals(TOTAL_SELL_PRICE), // TODO: Final Price. May need to migrate this data from calculations for older orders
      pricePerSquareFeetOfCoil: PRICE_PER_SQUARE_FEET_OF_COIL,
      pricePerLinealFootOfCoil: PRICE_PER_LINEAL_FOOT_OF_COIL,
    };
  };

  const calculatePanel = (quoteCopy, panel) => {
    let panelCopy = { ...panel };

    // Calculate Total FlatSheet Length Used Including Drop
    let {
      isTollForm,
      tollFormPricePerHit, // Default $2.00
      tollFormPanelRunFeePerLF, // Default $2.50
    } = quoteCopy;

    const WASTE = panelCopy.variables.waste;
    const EXCLUDE_PANEL_DROP_FROM_COST =
      panelCopy.variables.exclude_panel_drop_from_cost;
    const PANEL_FAB_RATE_LF_PER_HOUR = panelCopy.variables.panel_run_rate;
    const HOURLY_LABOR_RATE = panelCopy.variables.hourly_labor_cost;
    const LUMBER_RATE = panelCopy.variables.lumber_and_crating_rate;
    const GROSS_PROFIT_MARGIN = panelCopy.variables.gross_profit_margin;

    let COIL_COST_PER_SQFT =
      quoteCopy.coilCostPerSqFt != quoteCopy.coilCostPerSqFtOriginal
        ? parseFloat(quoteCopy.coilCostPerSqFt)
        : parseFloat(quoteCopy.coilCostPerSqFtOriginal);
    if (!COIL_COST_PER_SQFT) COIL_COST_PER_SQFT = 0;

    // If Toll-Form, material cost is zero
    if (isTollForm) COIL_COST_PER_SQFT = 0;

    const COIL_SIZE_NEEDED =
      Number(panel.panelWidth ?? 0) + Number(panel.panelTakeUp ?? 0); //inches

    //this is auto-calc'd BUT if it's 40" USER CAN OVERIDE TO 48 IN CASE 40" IS NOT IN-STOCK, BUT IF IT'S 48 YOU CANNOT OVERRIDE
    let masterCoilSizeNeededTemp = 40;
    if (COIL_SIZE_NEEDED <= 16 || COIL_SIZE_NEEDED > 20)
      masterCoilSizeNeededTemp = 48;
    // Override Material Cost if Exists
    const MASTER_COIL_SIZE_NEEDED =
      panel.overrides && panel.overrides.master_coil_size_needed
        ? Number(panel.overrides.master_coil_size_needed)
        : masterCoilSizeNeededTemp;
    // const MASTER_COIL_SIZE_NEEDED = masterCoilSizeNeededTemp;

    const COILS_CUT_FROM_MASTER_COIL = Math.floor(
      MASTER_COIL_SIZE_NEEDED / COIL_SIZE_NEEDED
    );

    const COIL_DROP =
      MASTER_COIL_SIZE_NEEDED - COIL_SIZE_NEEDED * COILS_CUT_FROM_MASTER_COIL;

    const getLF = (qty, feet, inches) =>
      parseFloat(qty) * (parseFloat(feet) + parseFloat(inches) / 12);

    // This is the lineal feet of finished coil, not master coil
    const LINEAL_FEET_OF_COIL_NEEDED_NOT_INCLUDING_WASTE = panel.cuts.reduce(
      (acc, cut) => (acc += getLF(cut.quantity, cut.feet, cut.inches)),
      0
    );

    const LINEAL_FEET_OF_COIL_NEEDED_INCLUDING_WASTE =
      LINEAL_FEET_OF_COIL_NEEDED_NOT_INCLUDING_WASTE +
      LINEAL_FEET_OF_COIL_NEEDED_NOT_INCLUDING_WASTE * WASTE;

    const SQFT_OF_COIL_NEEDED_INCLUDING_WASTE =
      LINEAL_FEET_OF_COIL_NEEDED_INCLUDING_WASTE * (COIL_SIZE_NEEDED / 12);

    const FINISHED_PANEL_SQFT =
      LINEAL_FEET_OF_COIL_NEEDED_NOT_INCLUDING_WASTE *
      (parseFloat(panel.panelWidth) / 12);

    // Save Original Material Cost in Calculations
    const USABLE_COIL_COST =
      COIL_COST_PER_SQFT * SQFT_OF_COIL_NEEDED_INCLUDING_WASTE;
    const SQFT_OF_DROP =
      LINEAL_FEET_OF_COIL_NEEDED_INCLUDING_WASTE * (COIL_DROP / 12);
    const DROP_COIL_COST = COIL_COST_PER_SQFT * SQFT_OF_DROP;
    // IF EXCLUDING PANEL DROP FROM  COST, THEN MATERIAL COST IS JUST THE USABLE COIL COST
    const MATERIAL_COST = EXCLUDE_PANEL_DROP_FROM_COST
      ? USABLE_COIL_COST
      : USABLE_COIL_COST + DROP_COIL_COST;

    // Override Material Cost if Exists
    const MATERIAL_COST_WITH_OVERRIDE =
      panel.overrides && panel.overrides.material_cost
        ? Number(panel.overrides.material_cost)
        : MATERIAL_COST;

    // Wrong the panel run rate is LF per hour not per minute
    // const TOTAL_ESTIMATED_LABOR_HOURS = $(
    //   "total_estimated_labor_hours",
    //   Math.ceil(
    //     LINEAL_FEET_OF_COIL_NEEDED_INCLUDING_WASTE /
    //       PANEL_FAB_RATE_LF_PER_MINUTE /
    //       60
    //   )
    // );

    const TOTAL_ESTIMATED_LABOR_HOURS = Math.ceil(
      LINEAL_FEET_OF_COIL_NEEDED_INCLUDING_WASTE / PANEL_FAB_RATE_LF_PER_HOUR
    );
    const TOTAL_ESTIMATED_LABOR_HOURS_WITH_OVERRIDE =
      panel.overrides && panel.overrides.total_estimated_labor_hours
        ? Number(panel.overrides.total_estimated_labor_hours)
        : TOTAL_ESTIMATED_LABOR_HOURS;

    // When Toll Forming, the cost is just the toll form rate times
    // the EXACT Lineal Footage (not including waste)
    const ESTIMATED_LABOR_COST = isTollForm
      ? LINEAL_FEET_OF_COIL_NEEDED_NOT_INCLUDING_WASTE *
        Number(tollFormPanelRunFeePerLF ?? 0)
      : TOTAL_ESTIMATED_LABOR_HOURS_WITH_OVERRIDE * HOURLY_LABOR_RATE;
    const ESTIMATED_LABOR_COST_WITH_OVERRIDE =
      panel.overrides && panel.overrides.total_estimated_labor_cost
        ? Number(panel.overrides.total_estimated_labor_cost)
        : ESTIMATED_LABOR_COST;

    // DON'T USE OVERRIDE MATERIAL COST FOR LUMBER. USE ORIGINAL MATERIAL COST
    const ESTIMATED_LUMBER_COST = isTollForm
      ? 0
      : (MATERIAL_COST + ESTIMATED_LABOR_COST_WITH_OVERRIDE) * LUMBER_RATE;
    // SAVE ORIGINAL LUMBER COST IN CALCULATION BUT USE OVERRIDE FOR DOWNSTREAM CALC IF EXISTS
    const ESTIMATED_LUMBER_COST_WITH_OVERRIDE =
      panel.overrides && panel.overrides.estimated_lumber_cost
        ? Number(panel.overrides.estimated_lumber_cost)
        : ESTIMATED_LUMBER_COST;

    const TOTAL_HARD_COST =
      MATERIAL_COST_WITH_OVERRIDE +
      ESTIMATED_LABOR_COST_WITH_OVERRIDE +
      ESTIMATED_LUMBER_COST_WITH_OVERRIDE;
    // SAVE ORIGINAL HARD COST BUT OVERRIDE IN DOWNSTREAM CALCULATIONS IF EXISTS
    const TOTAL_HARD_COST_WITH_OVERRIDE =
      panel.overrides && panel.overrides.total_hard_cost
        ? Number(panel.overrides.total_hard_cost)
        : TOTAL_HARD_COST;

    // If Toll Form, No GP
    const GROSS_PROFIT_DOLLARS = isTollForm
      ? 0
      : TOTAL_HARD_COST_WITH_OVERRIDE / (1 - GROSS_PROFIT_MARGIN) -
        TOTAL_HARD_COST_WITH_OVERRIDE;

    // TOTAL SELL PRICE AND PANEL PRICE PER SQUARE FOOT HAVE CONNECTED OVERRIDES
    // SAVE THE ACTUAL CALCULATED VALUES (OVERRIDE OR NON-OVERRIDE)
    let totalSellPriceBeforeOverride =
      TOTAL_HARD_COST_WITH_OVERRIDE + GROSS_PROFIT_DOLLARS;
    let panelPricePerSquareFootBeforeOverride =
      totalSellPriceBeforeOverride / FINISHED_PANEL_SQFT;

    let TOTAL_SELL_PRICE =
      panel.overrides && panel.overrides.total_sell_price
        ? Number(panel.overrides.total_sell_price)
        : totalSellPriceBeforeOverride;
    let PANEL_PRICE_PER_SQFT = TOTAL_SELL_PRICE / FINISHED_PANEL_SQFT;
    // IF PANEL PRICE PER SQFT HAS AN OVERRIDE, BACK CALCULATE THE TOTAL PRICE
    if (panel.overrides && panel.overrides.panel_price_per_square_foot) {
      PANEL_PRICE_PER_SQFT = Number(
        panel.overrides.panel_price_per_square_foot
      );

      TOTAL_SELL_PRICE = PANEL_PRICE_PER_SQFT * FINISHED_PANEL_SQFT;
    }
    // If Toll-Form Do not Apply GP At All
    let ACTUAL_GP_MARGIN = isTollForm
      ? 0
      : 1 - TOTAL_HARD_COST_WITH_OVERRIDE / TOTAL_SELL_PRICE;

    let ACTUAL_GP_DOLLARS = isTollForm
      ? 0
      : TOTAL_SELL_PRICE - TOTAL_HARD_COST_WITH_OVERRIDE;

    let calculations = {
      waste: toThreeDecimals(WASTE),
      coilSizeNeeded: COIL_SIZE_NEEDED,
      masterCoilSizeNeeded: MASTER_COIL_SIZE_NEEDED,
      coilsCutFromMaster: COILS_CUT_FROM_MASTER_COIL,
      coilDrop: COIL_DROP,
      linealFeetOfCoilNoWaste: toThreeDecimals(
        LINEAL_FEET_OF_COIL_NEEDED_NOT_INCLUDING_WASTE
      ),
      linealFeetOfCoilWithWaste: toThreeDecimals(
        LINEAL_FEET_OF_COIL_NEEDED_INCLUDING_WASTE
      ),
      squareFeetOfCoilNeededWithWaste: toThreeDecimals(
        SQFT_OF_COIL_NEEDED_INCLUDING_WASTE
      ),
      finishedPanelSquareFeet: toThreeDecimals(FINISHED_PANEL_SQFT),

      panelUsableCoilCost: toTwoDecimals(USABLE_COIL_COST),
      panelDropCoilCost: toTwoDecimals(DROP_COIL_COST),
      materialCost: toTwoDecimals(MATERIAL_COST),

      estimatedLaborHours: toTwoDecimals(TOTAL_ESTIMATED_LABOR_HOURS),
      estimatedLaborCost: toTwoDecimals(ESTIMATED_LABOR_COST),
      estimatedLumberCost: toTwoDecimals(ESTIMATED_LUMBER_COST),
      totalHardCost: toTwoDecimals(TOTAL_HARD_COST),
      grossProfitMargin: toFourDecimals(GROSS_PROFIT_MARGIN),
      grossProfitDollars: toTwoDecimals(GROSS_PROFIT_DOLLARS),
      totalSellPrice: toTwoDecimals(totalSellPriceBeforeOverride),
      panelPricePerSquareFoot: toTwoDecimals(
        panelPricePerSquareFootBeforeOverride
      ),

      actualGrossProfitMargin: toFourDecimals(ACTUAL_GP_MARGIN),
      actualGrossProfitDollars: toTwoDecimals(ACTUAL_GP_DOLLARS),
    };

    return {
      ...panelCopy,
      totalCost: TOTAL_SELL_PRICE, // TODO: DEPRECATED. Migrate to totalSellPrice
      calculations,
      // New
      totalSellPrice: TOTAL_SELL_PRICE,
      panelPricePerSquareFoot: PANEL_PRICE_PER_SQFT,
      finishedPanelSquareFeet: FINISHED_PANEL_SQFT,
      linealFeetOfCoilNoWaste: LINEAL_FEET_OF_COIL_NEEDED_NOT_INCLUDING_WASTE,
    };
  };

  // DONE-ISH till Matt reviews
  const calculateTrim = (quoteCopy, t) => {
    let trim = { ...t };

    let piecesKey = "";
    switch (trim.objectType) {
      case "TrimAndFlashing":
        piecesKey = "trimPieces";
        break;
      case "CopingCap":
        piecesKey = "capPieces";
        break;
      case "CopingCapCleat":
        piecesKey = "cleatPieces";
        break;
    }

    // TODO: trim.totalInchesDrop (from optimizer)
    // NEW 4/22/25
    // variables
    // TODO: SHOULD EXTRA_FLATS BE COUNTED AS DROPS?
    let {
      flats_used, // From Optimizer
      inches_of_drop, // From Optimizer
      extra_flats,
      gross_profit_margin,
      hits_per_minute,
      hourly_labor_cost,
      other_fs_cost,
    } = trim.variables;

    // Calculate Total FlatSheet Length Used Including Drop
    let {
      flatSheetCost,
      flatSheetSize,
      isTollForm,
      tollFormPricePerHit, // Default $2.00
      tollFormPanelRunFeePerLF, // Default $2.50
    } = quoteCopy;
    // TODO: Override flatSheetCost and flatSheetSize if override exists
    // IGNORE the quote flatsheet price if Coping Cap Cleat because it uses GALV FS. Use the override flat_sheet_cost
    if (trim.objectType === "CopingCapCleat") flatSheetCost = 0;
    if (
      t.overrides &&
      t.overrides.flat_sheet_cost !== undefined &&
      t.overrides.flat_sheet_cost !== null
    ) {
      // IMPORTANT: Selecting Galv Flat Sheet Option Overrides the flat_sheet_cost for CopingCapCleat
      flatSheetCost = t.overrides.flat_sheet_cost;
    }
    // IF TOLL FORM, FLAT SHEET COST IS ZERO REGARDLESS OF OVERRIDED
    if (isTollForm) {
      flatSheetCost = 0;
    }

    if (
      t.overrides &&
      t.overrides.flat_sheet_size !== undefined &&
      t.overrides.flat_sheet_size !== null
    ) {
      // IMPORTANT: Selecting Galv Flat Sheet Option Overrides the flat_sheet_cost for CopingCapCleat
      flatSheetSize = t.overrides.flat_sheet_size;
    }

    let totalInchesFlatSheets =
      Number(flats_used ?? 0) * Number(flatSheetSize ?? 0);
    let totalFsLengthUsedExcludingDrop =
      totalInchesFlatSheets - Number(inches_of_drop ?? 0);
    let totalInchesFlatSheetsWithExtraFlats =
      (Number(flats_used ?? 0) + Number(extra_flats ?? 0)) *
      Number(flatSheetSize ?? 0);

    // NEW 4/22/25
    // calculated
    let materialCostTotal = 0;
    let estimatedLaborHoursTotal = 0;
    let estimatedLaborCostTotal = 0;
    let materialAndLaborTotal = 0;
    let grossProfitDollarsTotal = 0;
    let trimTotalSellPriceCalculated = 0; // orderitem.totalCost copy into trim.calcs.totalSellPrice
    let trimTotalSellPriceOverrideCalculated = 0;

    // Calculate Total Material Cost
    // Spread out material cost later based on piece usage ratio (how long the piece is vs total combined length)
    materialCostTotal =
      Number(flatSheetCost ?? 0) *
        (Number(flats_used ?? 0) + Number(extra_flats ?? 0)) +
      Number(other_fs_cost ?? 0); // Add Extra flat sheet cost

    // Calculate Total Estimated Labor Hours and Cost
    // So that the calculation will only Ceiling up once instead of ceiling up on every piece
    // - THIS IS THE REASON THE THE LABOR WAS OFF ON THE OLD VERSION
    // Get the total Number of hits. Spread out cost later based on piece hit ratio
    let totalHits = 0;
    for (let i = 0; i < trim[piecesKey].length; i++) {
      let piece = trim[piecesKey][i];

      totalHits += Number(piece.quantity ?? 0) * Number(piece.hits ?? 0);
    }
    let hitsPerHour = Number(hits_per_minute) * 60;

    // Override Labor Hours If Exists
    estimatedLaborHoursTotal = parseFloat(Math.ceil(totalHits / hitsPerHour));
    let estimatedLaborHoursTotalUsed =
      trim.overrides && trim.overrides.total_estimated_labor_hours
        ? Number(trim.overrides.total_estimated_labor_hours)
        : parseFloat(Math.ceil(totalHits / hitsPerHour));

    if (isTollForm) {
      // If Toll-Forming, labor is based on price per hit
      estimatedLaborCostTotal = parseFloat(
        totalHits * Number(tollFormPricePerHit ?? 0)
      );
    } else {
      // If Single-Bill, labor is based on total labor hours * per hour rate
      estimatedLaborCostTotal = parseFloat(
        estimatedLaborHoursTotalUsed * Number(hourly_labor_cost ?? 0)
      );
    }

    // Calculate Price Per Piece and Piece Sell Pirce using Usage Ratio and Hit Ratio
    for (let i = 0; i < trim[piecesKey].length; i++) {
      let piece = trim[piecesKey][i];

      // Get Usage Ratio. Do not Include extra flats
      let totalPieceLength =
        Number(piece.quantity ?? 0) * Number(piece.stretchOut ?? 0);
      let usageRatio = 0;
      if (totalFsLengthUsedExcludingDrop)
        usageRatio = totalPieceLength / totalFsLengthUsedExcludingDrop; // DIVIDE BY ZERO POTENTIALLY
      // // Adjust Usage for Drops and Extra Flat Sheet
      let usageAdjustedForDrops = 0;
      if (totalInchesFlatSheetsWithExtraFlats)
        usageAdjustedForDrops =
          totalInchesFlatSheetsWithExtraFlats * usageRatio;
      let usedFlats = 0;
      if (flatSheetSize)
        usedFlats = usageAdjustedForDrops / Number(flatSheetSize ?? 0); // DIVIDE BY ZERO POTENTIALLY
      let materialCost = materialCostTotal * usageRatio;

      let pieceHitsTotal =
        Number(piece.quantity ?? 0) * Number(piece.hits ?? 0);
      let pieceHitRatio = pieceHitsTotal / totalHits;
      let estLaborHours = pieceHitRatio * estimatedLaborHoursTotalUsed;
      let estLaborCost = 0;
      if (isTollForm) {
        // If Toll-Forming, labor is based on price per hit
        estLaborCost = parseFloat(
          pieceHitsTotal * Number(tollFormPricePerHit ?? 0)
        );
      } else {
        // If Single-Bill, labor is based on total labor hours * per hour rate
        estLaborCost = parseFloat(
          estLaborHours * Number(hourly_labor_cost ?? 0)
        );
      }
      let materialAndLabor = (materialCost ?? 0) + (estLaborCost ?? 0);

      materialAndLaborTotal += materialAndLabor;

      // This is the calculated totalSellPrice which may be overriden (in variables)
      // IF TOLL FORM, DO NOT APPLY GP
      const pieceTotalSellPriceCalculated = isTollForm
        ? materialAndLabor
        : parseFloat(
            materialAndLabor / (1 - Number(gross_profit_margin ?? 0)) // DIVIDE BY ZERO POTENTIALLY (GP cant be 100%)
          );
      trimTotalSellPriceCalculated += pieceTotalSellPriceCalculated;
      // Set Piece Total Sell Price. Override if exists
      piece.totalSellPrice =
        piece.overrides &&
        piece.overrides.total_sell_price !== null &&
        piece.overrides.total_sell_price !== undefined
          ? toThreeDecimals(piece.overrides.total_sell_price)
          : toTwoDecimals(pieceTotalSellPriceCalculated);

      // For the total Override Price, just add up the actual pricing used for each piece
      trimTotalSellPriceOverrideCalculated += piece.totalSellPrice;
      // Set Price Per Piece. Override if exists
      const pricePerPiece = parseFloat(
        pieceTotalSellPriceCalculated / (piece.quantity ?? 1)
      );
      piece.pricePerPiece =
        piece.overrides &&
        piece.overrides.price_per_piece !== null &&
        piece.overrides.price_per_piece !== undefined
          ? toThreeDecimals(piece.overrides.price_per_piece)
          : toTwoDecimals(pricePerPiece);

      // This calculates GP from calculated total sell price.
      // If totalSellPrice is overriden, then the whole calculation is ignored.
      // And we just use the override final value
      const grossProfitDollars = parseFloat(
        pieceTotalSellPriceCalculated - materialAndLabor
      );
      grossProfitDollarsTotal += grossProfitDollars;

      // Most of these are no longer needed except the totals
      let pieceCalculations = {
        flatSheetSize: toThreeDecimals(flatSheetSize),
        flatSheetCost: toTwoDecimals(flatSheetCost),
        // piecesYieldedPerFlatSheet: toThreeDecimals(PIECES_YIELDED_PER_FLAT_SHEET),
        flatSheetsNeeded: toThreeDecimals(usedFlats),
        // inchesOfDropPerFlatSheet: toThreeDecimals(INCHES_OF_DROP_PER_FLAT),
        // numberOfDrops: toThreeDecimals(NUMBER_OF_DROPS),
        estimatedLaborHours: toTwoDecimals(estLaborHours),
        estimatedLaborCost: toTwoDecimals(estLaborCost),
        materialCost: toTwoDecimals(materialCost),
        materialAndLabor: toTwoDecimals(materialAndLabor),
        // grossProfitMargin: toFourDecimals(gross_profit_margin), // This has been moved to variables
        grossProfitDollars: toTwoDecimals(grossProfitDollars),
        totalSellPrice: toTwoDecimals(pieceTotalSellPriceCalculated),
        pricePerPiece: toTwoDecimals(pricePerPiece),
      };
      piece.calculations = pieceCalculations;
    }

    materialAndLaborTotal =
      (estimatedLaborCostTotal ?? 0) + (materialCostTotal ?? 0);

    let trimCalculations = {
      totalSellPrice: trimTotalSellPriceCalculated, // trim.calcs.totalSellPrice and trim.totalCost are the same
      materialAndLabor: materialAndLaborTotal,
      grossProfitDollars: grossProfitDollarsTotal,
      estimatedLaborCost: estimatedLaborCostTotal,
      estimatedLaborHours: estimatedLaborHoursTotal,
      materialCost: materialCostTotal,
    };
    // Save Totals on Trim
    // TODO: Final Price. May need to migrate this data from calculations for older orders
    trim.totalCost = toTwoDecimals(
      trimTotalSellPriceOverrideCalculated ?? trimTotalSellPriceCalculated
    ); // TODO: DEPRECATED migrate to totalSellPrice
    trim.totalSellPrice = toTwoDecimals(
      trimTotalSellPriceOverrideCalculated ?? trimTotalSellPriceCalculated
    );

    trim.trimCalculations = trimCalculations;
    // trim.variables = trimVariables;  // Variables are directly updated via the UI textfield, inputs, etc.
    return trim;
  };

  // DONE-ISH till Matt reviews
  const calculateSplicePlate = (quoteCopy, t) => {
    let trim = { ...t };

    // TODO: trim.totalInchesDrop (from optimizer)
    // NEW 4/22/25
    // variables
    let {
      flats_used, // From Optimizer
      inches_of_drop, // From Optimizer
      extra_flats,
      gross_profit_margin,
      hits_per_minute,
      hourly_labor_cost,
      other_fs_cost,
    } = trim.variables;

    // Calculate Total FlatSheet Length Used Including Drop
    let {
      flatSheetCost,
      flatSheetSize,
      isTollForm,
      tollFormPricePerHit, // Default $2.00
      tollFormPanelRunFeePerLF, // Default $2.50
    } = quoteCopy;
    // Override flatSheetCost and flatSheetSize if override exists
    if (
      t.overrides &&
      t.overrides.flat_sheet_cost !== undefined &&
      t.overrides.flat_sheet_cost !== null
    ) {
      flatSheetCost = t.overrides.flat_sheet_cost;
    }
    // IF TOLL FORM, FLAT SHEET COST IS ZERO REGARDLESS OF OVERRIDED
    if (isTollForm) {
      flatSheetCost = 0;
    }

    if (
      t.overrides &&
      t.overrides.flat_sheet_size !== undefined &&
      t.overrides.flat_sheet_size !== null
    ) {
      flatSheetSize = t.overrides.flat_sheet_size;
    }

    // NEW 4/22/25
    // calculated
    let materialCostTotal = 0;
    let estimatedLaborHoursTotal = 0;
    let estimatedLaborCostTotal = 0;
    let materialAndLaborTotal = 0;
    let grossProfitDollarsTotal = 0;
    let totalCost = 0; // orderitem.totalCost copy into trim.calcs.totalSellPrice
    let overrideTotalCost = 0;

    // CALCULATE TOTAL MATERIAL AND TOTAL LABOR COST
    // - Determine total flats and total hits for every piece first
    let totalFlats = 0;
    let totalHits = 0;
    let totalAreaUsed = 0;
    for (let i = 0; i < trim.plates.length; i++) {
      let piece = trim.plates[i];

      // CALCULATE PIECES_PER_FLAT_SHEET
      let FS_LENGTH = 120; //10 ft or 120 inches
      let PIECES_YIELDED_PER_FLAT_SHEET =
        Math.floor(Number(flatSheetSize ?? 0) / Number(piece.stretchOut ?? 1)) *
        (Number(FS_LENGTH ?? 0) / Number(piece.length ?? 1));
      // CALCULATE TOTAL FLATS FOR THIS PIECE
      let usedFlats = Math.ceil(
        Number(piece.quantity) / PIECES_YIELDED_PER_FLAT_SHEET
      );

      // CALCULATE PIECE AREA
      let pieceArea = Number(piece.stretchOut ?? 0) * Number(piece.length ?? 0);
      let totalPieceArea = pieceArea * Number(piece.quantity ?? 0);

      // GET TOTAL HITS
      totalFlats += usedFlats;
      totalHits += Number(piece.quantity ?? 0) * Number(piece.hits ?? 0);
      totalAreaUsed += totalPieceArea;

      // Most of these are no longer needed except the totals
      let pieceCalculations = {
        flatSheetSize: toThreeDecimals(flatSheetSize),
        flatSheetCost: toTwoDecimals(flatSheetCost),
        piecesYieldedPerFlatSheet: toThreeDecimals(
          PIECES_YIELDED_PER_FLAT_SHEET
        ),
        flatSheetsNeeded: toThreeDecimals(usedFlats),
      };
      piece.calculations = pieceCalculations;
    }
    // - CALCULATE TOTAL MATERIAL COST: OVERRIDE IF EXISTS
    if (
      t.overrides &&
      t.overrides.flat_sheets_needed !== undefined &&
      t.overrides.flat_sheets_needed !== null
    ) {
      materialCostTotal =
        Number(t.overrides.flat_sheets_needed ?? 0) *
          Number(flatSheetCost ?? 0) +
        Number(other_fs_cost ?? 0);
    } else {
      materialCostTotal =
        Number(totalFlats ?? 0) * Number(flatSheetCost ?? 0) +
        Number(other_fs_cost ?? 0);
    }

    // - CALCULATE TOTAL LABOR COST: Override Labor Hours If Exists
    let hitsPerHour = Number(hits_per_minute) * 60;
    estimatedLaborHoursTotal = parseFloat(Math.ceil(totalHits / hitsPerHour));
    let estimatedLaborHoursTotalUsed =
      trim.overrides && trim.overrides.total_estimated_labor_hours
        ? Number(trim.overrides.total_estimated_labor_hours)
        : parseFloat(Math.ceil(totalHits / hitsPerHour));

    if (isTollForm) {
      // If Toll-Forming, labor is based on price per hit
      estimatedLaborCostTotal = parseFloat(
        totalHits * Number(tollFormPricePerHit ?? 0)
      );
    } else {
      // If Single-Bill, labor is based on total labor hours * per hour rate
      estimatedLaborCostTotal = parseFloat(
        estimatedLaborHoursTotalUsed * Number(hourly_labor_cost ?? 0)
      );
    }

    // CALCULATE PIECE PRICE
    // - Spread the cost of labor by piece hits vs total hits
    // - Spread the cost of material by piece area vs total area
    for (let i = 0; i < trim.plates.length; i++) {
      let piece = trim.plates[i];

      // Calculate Usage Ratio and Material Cost
      let pieceAreaTotal =
        Number(piece.quantity ?? 0) *
        Number(piece.stretchOut ?? 0) *
        Number(piece.length ?? 0);
      let usageRatio = pieceAreaTotal / totalAreaUsed;
      let materialCost = materialCostTotal * usageRatio;

      // Calculate Hit Ratio and Labor Cost
      let pieceHitsTotal =
        Number(piece.quantity ?? 0) * Number(piece.hits ?? 0);
      let pieceHitRatio = pieceHitsTotal / totalHits;
      let estLaborHours = pieceHitRatio * estimatedLaborHoursTotalUsed;

      let estLaborCost = 0;
      if (isTollForm) {
        // If Toll-Forming, labor is based on price per hit
        estLaborCost = parseFloat(
          pieceHitsTotal * Number(tollFormPricePerHit ?? 0)
        );
      } else {
        // If Single-Bill, labor is based on total labor hours * per hour rate
        estLaborCost = parseFloat(
          estLaborHours * Number(hourly_labor_cost ?? 0)
        );
      }

      let materialAndLabor = (materialCost ?? 0) + (estLaborCost ?? 0);

      materialAndLaborTotal += materialAndLabor;

      // This is the calculated totalSellPrice which may be overriden (in variables)
      const totalSellPrice = parseFloat(
        materialAndLabor / (1 - Number(gross_profit_margin ?? 0)) // DIVIDE BY ZERO POTENTIALLY (GP cant be 100%)
      );
      totalCost += totalSellPrice;
      // Set Piece Total Sell Price. Override if exists
      piece.totalSellPrice =
        piece.overrides &&
        piece.overrides.total_sell_price !== null &&
        piece.overrides.total_sell_price !== undefined
          ? toThreeDecimals(piece.overrides.total_sell_price)
          : toTwoDecimals(totalSellPrice);

      overrideTotalCost += piece.totalSellPrice;
      // Set Price Per Piece. Override if exists
      const pricePerPiece = parseFloat(totalSellPrice / (piece.quantity ?? 1));
      piece.pricePerPiece =
        piece.overrides &&
        piece.overrides.price_per_piece !== null &&
        piece.overrides.price_per_piece !== undefined
          ? toThreeDecimals(piece.overrides.price_per_piece)
          : toTwoDecimals(pricePerPiece);

      const grossProfitDollars = parseFloat(totalSellPrice - materialAndLabor);
      grossProfitDollarsTotal += grossProfitDollars;

      // Most of these are no longer needed except the totals

      let pieceCalculations = {
        ...piece.calculations,
        estimatedLaborHours: toTwoDecimals(estLaborHours),
        estimatedLaborCost: toTwoDecimals(estLaborCost),
        materialCost: toTwoDecimals(materialCost),
        materialAndLabor: toTwoDecimals(materialAndLabor),
        grossProfitDollars: toTwoDecimals(grossProfitDollars),
        totalSellPrice: toTwoDecimals(totalSellPrice),
        pricePerPiece: toTwoDecimals(pricePerPiece),
      };
      piece.calculations = pieceCalculations;
    }

    materialAndLaborTotal =
      (estimatedLaborCostTotal ?? 0) + (materialCostTotal ?? 0);

    let trimCalculations = {
      totalSellPrice: totalCost, // trim.calcs.totalSellPrice and trim.totalCost are the same
      materialAndLabor: materialAndLaborTotal,
      grossProfitDollars: grossProfitDollarsTotal,
      estimatedLaborCost: estimatedLaborCostTotal,
      estimatedLaborHours: estimatedLaborHoursTotal,
      materialCost: materialCostTotal,
    };
    // Save Totals on Trim
    // TODO: Final Price. May need to migrate this data from calculations for older orders
    trim.totalCost = toTwoDecimals(overrideTotalCost ?? totalCost); // TODO: DEPRECATED migrate to totalSellPrice
    trim.totalSellPrice = toTwoDecimals(overrideTotalCost ?? totalCost);
    trim.trimCalculations = trimCalculations;
    // trim.variables = trimVariables;  // Variables are directly updated via the UI textfield, inputs, etc.
    // Save Totals on Trim
    trim.variables = { ...trim.variables, flats_used: totalFlats };
    return trim;
  };

  // DONE-ISH till Matt reviews
  const calculateBenchWork = (quoteCopy, t) => {
    let trim = { ...t };

    let {
      // flats_used, // From Optimizer
      inches_of_drop, // From Optimizer
      extra_flats,
      gross_profit_margin,
      hits_per_minute,
      hourly_labor_cost,
    } = trim.variables;

    // Calculate Total FlatSheet Length Used Including Drop
    let {
      flatSheetCost,
      flatSheetSize,
      isTollForm,
      tollFormPricePerHit, // Default $2.00
      tollFormPanelRunFeePerLF, // Default $2.50
    } = quoteCopy;
    // Override flatSheetCost and flatSheetSize if override exists
    // IGNORE the quote flatsheet price if Coping Cap Cleat because it uses GALV FS. Use the override flat_sheet_cost
    if (
      t.overrides &&
      t.overrides.flat_sheet_cost !== undefined &&
      t.overrides.flat_sheet_cost !== null
    ) {
      flatSheetCost = t.overrides.flat_sheet_cost;
    }
    // IF TOLL FORM, FLAT SHEET COST IS ZERO REGARDLESS OF OVERRIDED
    if (isTollForm) {
      flatSheetCost = 0;
    }

    if (
      t.overrides &&
      t.overrides.flat_sheet_size !== undefined &&
      t.overrides.flat_sheet_size !== null
    ) {
      flatSheetSize = t.overrides.flat_sheet_size;
    }

    // NEW 4/22/25
    // calculated
    let materialCostTotal = 0;
    let estimatedLaborHoursTotal = 0;
    let estimatedLaborCostTotal = 0;
    let materialAndLaborTotal = 0;
    let grossProfitDollarsTotal = 0;
    let totalCost = 0;
    let overrideTotalCost = 0;
    let flatsUsedTotal = 0;

    // Calculate Price Per Piece and Piece Sell Pirce using Usage Ratio and Hit Ratio
    for (let i = 0; i < trim.benchWorkPieces.length; i++) {
      let piece = trim.benchWorkPieces[i];
      let usedFlats = Number(piece.variables.flats_used ?? 0);
      let estLaborHours = Number(piece.variables.estimated_labor_hours ?? 0);

      let materialCost = Number(flatSheetCost ?? 0) * usedFlats;
      // If Toll-Form enter the estimate labor cost directly
      let estLaborCost = isTollForm
        ? parseFloat(
            Number(piece.variables.bench_work_toll_form_labor_cost ?? 0)
          )
        : parseFloat(estLaborHours * Number(hourly_labor_cost ?? 0));

      let materialAndLabor = (materialCost ?? 0) + (estLaborCost ?? 0);
      // This is the calculated totalSellPrice which may be overriden (in variables)
      const totalSellPrice = parseFloat(
        materialAndLabor / (1 - Number(gross_profit_margin ?? 0)) // DIVIDE BY ZERO POTENTIALLY (GP cant be 100%)
      );

      // Set Piece Total Sell Price. Override if exists
      piece.totalSellPrice =
        piece.overrides &&
        piece.overrides.total_sell_price !== null &&
        piece.overrides.total_sell_price !== undefined
          ? toThreeDecimals(piece.overrides.total_sell_price)
          : toTwoDecimals(totalSellPrice);

      // Set Price Per Piece. Override if exists
      const pricePerPiece = parseFloat(totalSellPrice / (piece.quantity ?? 1));
      piece.pricePerPiece =
        piece.overrides &&
        piece.overrides.price_per_piece !== null &&
        piece.overrides.price_per_piece !== undefined
          ? toThreeDecimals(piece.overrides.price_per_piece)
          : toTwoDecimals(pricePerPiece);

      const grossProfitDollars = parseFloat(totalSellPrice - materialAndLabor);

      // Most of these are no longer needed except the totals
      let pieceCalculations = {
        flatSheetSize: toThreeDecimals(flatSheetSize),
        flatSheetCost: toTwoDecimals(flatSheetCost),
        // piecesYieldedPerFlatSheet: toThreeDecimals(PIECES_YIELDED_PER_FLAT_SHEET),
        flatSheetsNeeded: toThreeDecimals(usedFlats),
        // inchesOfDropPerFlatSheet: toThreeDecimals(INCHES_OF_DROP_PER_FLAT),
        // numberOfDrops: toThreeDecimals(NUMBER_OF_DROPS),
        estimatedLaborHours: toTwoDecimals(estLaborHours),
        estimatedLaborCost: toTwoDecimals(estLaborCost),
        materialCost: toTwoDecimals(materialCost),
        materialAndLabor: toTwoDecimals(materialAndLabor),
        // grossProfitMargin: toFourDecimals(gross_profit_margin), // This has been moved to variables
        grossProfitDollars: toTwoDecimals(grossProfitDollars),
        totalSellPrice: toTwoDecimals(totalSellPrice),
        pricePerPiece: toTwoDecimals(pricePerPiece),
      };
      piece.calculations = pieceCalculations;
      // Reset Toll-Form Labor Cost IF NOT Toll-Formed
      piece.variables = {
        ...piece.variables,
        bench_work_toll_form_labor_cost: isTollForm
          ? piece.variables.bench_work_toll_form_labor_cost
          : 0,
      };

      // Update OrderItem Totals
      materialCostTotal += materialCost;
      estimatedLaborHoursTotal += estLaborHours;
      estimatedLaborCostTotal += estLaborCost;
      materialAndLaborTotal += materialAndLabor;
      grossProfitDollarsTotal += grossProfitDollars;
      totalCost += totalSellPrice;
      overrideTotalCost += piece.totalSellPrice;
      flatsUsedTotal += usedFlats;
    }

    let benchWorkCalculations = {
      totalSellPrice: totalCost, // trim.calcs.totalSellPrice and trim.totalCost are the same
      materialAndLabor: materialAndLaborTotal,
      grossProfitDollars: grossProfitDollarsTotal,
      totalEstimatedLaborCost: estimatedLaborCostTotal,
      totalEstimatedLaborHours: estimatedLaborHoursTotal,
      materialCost: materialCostTotal,
    };
    // Save Totals on Trim
    // TODO: Final Price. May need to migrate this data from calculations for older orders
    trim.totalCost = toTwoDecimals(overrideTotalCost ?? totalCost); // TODO: DEPRECATED migrated to totalSellPrice
    trim.totalSellPrice = toTwoDecimals(overrideTotalCost ?? totalCost);
    trim.benchWorkCalculations = benchWorkCalculations;

    trim.variables = { ...trim.variables, flats_used: flatsUsedTotal };
    return trim;
  };

  const authHeader = {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  };

  // Used for adding default accessory calculation value
  const getAccessoriesCatalog = async () => {
    try {
      const response = await ezorder.get(`/app/accessories`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      const items = response.data.accessories;
      return [...items];
    } catch (error) {
      console.log(error);
    }
  };

  const calculateMaterialCost = (quantity, costPerItem) => {
    let materialCost = toTwoDecimals(quantity * costPerItem);
    return materialCost;
  };
  const calculateMarkUp = (quantity, price, markupPercentage) => {
    let markUp = toTwoDecimals(quantity * price * (markupPercentage / 100));
    return markUp;
  };
  const calculateTotalSellPrice = (quantity, costPerItem, markupPercentage) => {
    let totalSellPrice = toTwoDecimals(
      quantity * costPerItem * (1 + markupPercentage / 100)
    );
    return totalSellPrice;
  };

  const getQuoteById = async (id) => {
    try {
      setLoading(true);
      const response = await ezorder.get(`/admin/orders/${id}`, authHeader);

      // Add Default Variables and Calculations if Missing (Quote Request by Customer)
      if (response.status === 200 || response.status == 201) {
        let q = response.data.order;

        let defaultVariables = await getDefaultVariables();
        // If orderItem.calculations is undefined or null (Old Version of Order or Customer Requested), then re-calculate
        let itemsCopy = [...q.items];
        for (let i = 0; i < itemsCopy.length; i++) {
          let orderItem = itemsCopy[i];
          if (!orderItem.variables)
            orderItem.variables = { ...defaultVariables }; // Add Default Variables if doesnt exists (customer sent quote)
          switch (orderItem.objectType) {
            case "FlatSheet":
              if (!orderItem.flatSheetCalculations)
                itemsCopy[i] = calculateFlatSheet(q, orderItem);
              break;
            case "Coil":
              if (!orderItem.coilCalculations)
                itemsCopy[i] = calculateCoil(q, orderItem);
              break;
            case "Panel":
              for (let cut of orderItem.cuts) {
                // Add default variables and empty calculations to each cut
                if (!cut.variables) cut.variables = { ...defaultVariables };
              }
              if (!orderItem.calculations) {
                let c_panel = calculatePanel(q, orderItem);
                itemsCopy[i] = c_panel;
              }
              break;
            case "TrimAndFlashing":
              for (let piece of orderItem.trimPieces) {
                // Add default variables and empty calculations to each piece
                if (!piece.variables) piece.variables = { ...defaultVariables };
              }
              if (!orderItem.trimCalculations)
                itemsCopy[i] = calculateTrim(q, orderItem);
              break;
            case "BenchWork":
              for (let piece of orderItem.benchWorkPieces) {
                // Add default variables and empty calculations to each piece
                if (!piece.variables) piece.variables = { ...defaultVariables };
              }
              if (!orderItem.benchWorkCalculations)
                itemsCopy[i] = calculateBenchWork(q, orderItem);
              break;
            case "CopingCap":
              for (let piece of orderItem.capPieces) {
                // Add default variables and empty calculations to each piece
                if (!piece.variables) piece.variables = { ...defaultVariables };
              }
              if (!orderItem.trimCalculations)
                itemsCopy[i] = calculateTrim(q, orderItem);
              break;
            case "CopingCapCleat":
              for (let piece of orderItem.cleatPieces) {
                // Add default variables and empty calculations to each piece
                if (!piece.variables) piece.variables = { ...defaultVariables };
              }
              if (!orderItem.trimCalculations)
                itemsCopy[i] = calculateTrim(q, orderItem);
              break;
            case "SplicePlate":
              for (let piece of orderItem.plates) {
                // Add default variables and empty calculations to each piece
                if (!piece.variables) piece.variables = { ...defaultVariables };
              }
              if (!orderItem.trimCalculations)
                itemsCopy[i] = calculateSplicePlate(q, orderItem);
              break;
            case "Accessory":
              let catalog = await getAccessoriesCatalog();

              // Add default variables and empty calculations to each piece
              for (let item of orderItem.items) {
                // Update Each Item w/ Reducer if the calculation object doesnt exist
                if (!item.calculations) {
                  // console.log(
                  //   "ADD DEFAULT ACCESSORY CALC OBJECT BECAUSE MISSING",
                  //   item
                  // );
                  // Find the Matching Catalog Item and Extract default values
                  const matchingCatalogItem = catalog.find(
                    (catalogItem) => catalogItem.name === item.name
                  );
                  // console.log("MATCHING CATALOG ITEM", matchingCatalogItem);
                  let materialCost = calculateMaterialCost(
                    Number(item.quantity),
                    Number(matchingCatalogItem.cost)
                  );

                  let markUp = calculateMarkUp(
                    Number(item.quantity),
                    Number(matchingCatalogItem.cost),
                    Number(matchingCatalogItem.markupPercentage)
                  );

                  let totalSellPrice = calculateTotalSellPrice(
                    Number(item.quantity),
                    Number(matchingCatalogItem.cost),
                    Number(matchingCatalogItem.markupPercentage)
                  );

                  item.price = Number(matchingCatalogItem.cost);
                  item.calculations = {
                    markUpPercentage: Number(
                      matchingCatalogItem.markupPercentage
                    ),
                    markUp: Number(markUp),
                    materialCost: Number(materialCost),
                    totalSellPrice: Number(totalSellPrice),
                  };
                  item.totalSellPrice = Number(totalSellPrice);
                  item.pricePerPiece =
                    item.quantity != undefined && item.quantity != null
                      ? Number(totalSellPrice) / Number(item.quantity)
                      : 0;
                }
              }
              // Set Total For Accessories
              let accessoryTotal = 0;
              for (let item of orderItem.items) {
                accessoryTotal += Number(item.totalSellPrice ?? 0);
              }
              orderItem.totalCost = accessoryTotal;
              orderItem.totalSellPrice = accessoryTotal;

              // Accessory Calculations Are done inside the Accessory component
              // There are no overrides so recalculating is unnecessary
              break;
          }
        }
        // Calculate Out the Door Cost
        // - item totalSellPrice + skid charge
        let outTheDoorCost = 0;
        for (let item of itemsCopy) {
          outTheDoorCost += Number(item.totalSellPrice);
        }
        outTheDoorCost += Number(q.skidCharge ?? 0);

        let recalculatedQuote = {
          ...q,
          items: itemsCopy,
          outTheDoorCost,
        };
        setQuote(recalculatedQuote);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (quoteId) {
      // quoteId is not null. Load the order and set it as quote detail
      getQuoteById(quoteId);
    } else {
      // no quoteId passed in. Set quote as empty object
      setQuote(EMPTY_QUOTE);
    }
  }, [quoteId]);

  useEffect(() => {
    console.log("QUOTE CHANGED", quote);
  }, [quote]);

  return (
    <Box>
      <Box sx={{ display: "flex", flexDirection: "row" }}>
        <Box sx={{ flex: 1 }}>
          {quote && quote.id && (
            <OrderTitle order={quote} includeStatus={true} />
          )}
        </Box>
        <Typography variant="h5">
          Out The Door Cost:{" "}
          {currencyFormatNoDecimals(
            Math.round(
              Number(quote && quote.outTheDoorCost ? quote.outTheDoorCost : 0)
            )
          )}
        </Typography>
      </Box>

      <TopSection quote={quote} onUpdateQuote={onUpdateQuote} />
      <Divider sx={{ marginTop: "16px", marginBottom: "16px" }} />
      {quote &&
        quote.project &&
        quote.company &&
        quote.estimateRequestedBy &&
        variables && (
          <Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: isMediumScreen ? "column" : "row",
              }}
            >
              {quote.quoteType !== "Free Text" && (
                <>
                  <Box sx={{ flex: 1, maxWidth: "200px" }}>
                    <SelectProducts
                      quote={quote}
                      onUpdateQuote={onUpdateQuote}
                      defaultVariables={variables}
                    />
                  </Box>
                  {isMediumScreen ? (
                    <Divider sx={{ marginTop: "16px", marginBottom: "16px" }} />
                  ) : (
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{ marginLeft: "16px", marginRight: "32px" }}
                    />
                  )}
                </>
              )}

              <Box sx={{ flex: 1 }}>
                <MaterialSection
                  quote={quote}
                  onUpdateQuote={onUpdateQuote}
                  defaultVariables={variables}
                />
              </Box>
            </Box>
            {quote.quoteType === "Free Text" ? (
              <Box sx={{}}>
                <Divider sx={{ marginTop: "16px", marginBottom: "16px" }} />
                <Typography variant="h6" sx={{ marginBottom: "8px" }}>
                  Quote Summary / Notes
                </Typography>
                <TextField
                  multiline
                  rows={20}
                  variant="outlined"
                  label="Quote Summary / Notes"
                  id="quoteNotes"
                  name="quoteNotes"
                  fullWidth
                  value={quote.quoteNotes}
                  onChange={(e) => {
                    onUpdateQuote({ quoteNotes: e.target.value });
                  }}
                />
              </Box>
            ) : (
              <>
                <Box sx={{}}>
                  <Divider sx={{ marginTop: "16px", marginBottom: "16px" }} />
                  <Typography variant="h6" sx={{ marginBottom: "8px" }}>
                    Add Skid Charge
                  </Typography>
                  <TextField
                    onFocus={(e) => e.target.select()}
                    label="Skid Charge"
                    value={`${quote?.skidCharge}`}
                    type="number"
                    onWheel={(event) => {
                      event.preventDefault();
                    }}
                    size="small"
                    onChange={(e) => {
                      let update = {
                        skidCharge: toTwoDecimals(e.target.value),
                      };
                      onUpdateQuote(update);
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment
                          position="Start"
                          sx={{ marginRight: "4px" }}
                        >
                          $
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                <Items
                  quote={quote}
                  onUpdateQuote={onUpdateQuote}
                  defaultVariables={variables}
                />
              </>
            )}

            <div
              style={{
                position: "fixed",
                bottom: "20px",
                right: "50px",
                display: "flex",
                flexDirection: "column",
                gap: 16,
              }}
            >
              {(!quote.orderStatus ||
                quote.orderStatus.name == "SAVED" ||
                quote.orderStatus.name == "QUOTE_REQUESTED") && (
                <Fab
                  disabled={loading || saving}
                  onClick={() => {
                    onSave(quote);
                  }}
                  variant="extended"
                  color="success"
                >
                  {saveButtonTitle}
                </Fab>
              )}
            </div>
          </Box>
        )}
    </Box>
  );
};

export default QuoteForm;
